import { useEffect, useRef, type ReactNode } from "react";
import "./Drawer.css";

export type DrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: "sm" | "md" | "lg";
  density?: "compact" | "default";
};

export function Drawer({
  isOpen,
  onClose,
  title,
  children,
  footer,
  width = "md",
  density = "compact",
}: DrawerProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const root = rootRef.current;
    if (!root) return;
    const focusable = root.querySelector<HTMLElement>(
      "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
    );
    focusable?.focus();
  }, [isOpen]);

  if (!isOpen) return null;

  const widthClass = `drawer--${width}`;
  const densityClass = density === "default" ? "form-default" : "form-compact";

  return (
    <>
      <div
        className="drawer-backdrop"
        data-testid="drawer-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        ref={rootRef}
        className={`drawer ${widthClass} ${densityClass}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="drawer__header">
          <h2 className="drawer__title">{title}</h2>
          <button
            type="button"
            className="drawer__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div className="drawer__body">{children}</div>
        {footer ? <footer className="drawer__footer">{footer}</footer> : null}
      </aside>
    </>
  );
}
